import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import get from 'lodash/get';
import {useTranslation} from 'react-i18next';
import {push} from 'connected-react-router';
import {useSMCSnackbar} from '../../../utils/hooks';
import {setProperty, getErrorCode, isStringI18n, extractI18nString} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import store from '../../../store';
import {SMCPaths} from '../../../conf/Routes';
import {AdministratorGetSchema} from '../../../api/DBSchema/Administrator';
import AdministratorGeneral from './InnerPages/AdministratorGeneral';
import AdministratorExpiration from './InnerPages/AdministratorExpiration';
import generalIcon from '../../../assets/images/general.png';
import hourglassIcon from '../../../assets/images/hourglass.png';
import megaphoneIcon from '../../../assets/images/megaphone.png';
import padlockIcon from '../../../assets/images/padlock.png';
import pageIcon from '../../../assets/images/page.png';
import AdministratorContact from './InnerPages/AdministratorContact';
import AdministratorTwoStepAuth from './InnerPages/AdministratorTwoStepAuth';
import AdministratorStatus from './InnerPages/AdministratorStatus';
import AdministratorDomains from './InnerPages/AdministratorDomains';
import {connectedIcon, disconnectedIcon} from '../../../assets/images/gateway';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useAdministratorEditor(administratorId, defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar, enqueueLogoutSnackbar, enqueueSuccessSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origAdministratorObject, setOrigAdministratorObject] = useState({});
    const [administratorObject, setAdministratorObject] = useState({});

    const [minimumPasswordLength, setMinimumPasswordLength] = useState();
    const [maximumPasswordLength, setMaximumPasswordLength] = useState();

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const statusIcon = useMemo(() => {
        if (administratorObject && administratorObject[AdministratorGetSchema.pages.status.pageId]) {
            const administratorStatusList = administratorObject[AdministratorGetSchema.pages.status.pageId][AdministratorGetSchema.pages.status.statusDataList];
            if (Array.isArray(administratorStatusList) && administratorStatusList.length > 0) {
                return connectedIcon;
            } else {
                return disconnectedIcon;
            }
        } else {
            return disconnectedIcon;
        }
    }, [administratorObject]);

    // Does admin have service domain page in editor
    const hasServiceDomainPage = useMemo(() => {
        const adminRole = get(administratorObject, [AdministratorGetSchema.pages.general.pageId, AdministratorGetSchema.pages.general.role]);
        return (
            adminRole === AdministratorGetSchema.pages.general.roleValues.multiDomain ||
            adminRole === AdministratorGetSchema.pages.general.roleValues.multiDomainReadOnly
        );
    }, [administratorObject]);

    const editorPages = useMemo(() => ([
        { id: AdministratorGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: AdministratorGeneral, helpId: helpContextPageUrl.administratorsGeneral },        
        { id: AdministratorGetSchema.pages.expiration.pageId, title: t('SMP.USERS.TABLE.INNER_MENU.EXPIRATION'), icon: hourglassIcon, pageElement: AdministratorExpiration, helpId: helpContextPageUrl.administratorExpiration },        
        { id: AdministratorGetSchema.pages.contact.pageId, title: t('SMP.USERS.TABLE.INNER_MENU.CONTACT'), icon: megaphoneIcon, pageElement: AdministratorContact, helpId: helpContextPageUrl.administratorContact },        
        { id: AdministratorGetSchema.pages.twoStepAuth.pageId, title: t('SMP.TREE_NODE_INNER.TWO_STEP_AUTHENTICATION'), icon: padlockIcon, pageElement: AdministratorTwoStepAuth, helpId: helpContextPageUrl.administratorResults },
        { id: AdministratorGetSchema.pages.status.pageId, title: t('SMP.USERS.TABLE.INNER_MENU.STATUS'), icon: statusIcon, pageElement: AdministratorStatus, helpId: helpContextPageUrl.administratorStatus },        
        { id: AdministratorGetSchema.pages.serviceDomains.pageId, title: t('SMP.ADMINISTRATORS.INNER_MENU.SERVICE_DOMAINS'), icon: pageIcon, pageElement: AdministratorDomains, hide: !hasServiceDomainPage, helpId: helpContextPageUrl.administratorDomains }
    ]), [hasServiceDomainPage, statusIcon, t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchAdministrator = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            //const fetchAdministratorResp = await Api.fetchSingleObject(ObjectTypes.ADMINISTRATOR, administratorId);
            const [fetchAdministratorResp,getSessionDataResp] = await Promise.all([
                Api.fetchSingleObject(ObjectTypes.ADMINISTRATOR, administratorId),
                Api.getSessionData({attributes: [
                    SessionDataGetSchema.minimumPasswordLength,
                    SessionDataGetSchema.maximumPasswordLength
                ]})
            ]);
            const administrator = fetchAdministratorResp.data.data.administrator;
            const minimumPasswordLength = getSessionDataResp.data[SessionDataGetSchema.minimumPasswordLength];
            const maximumPasswordLength = getSessionDataResp.data[SessionDataGetSchema.maximumPasswordLength];
            // Validate server response
            if (!administrator) {
                console.error('Get administrator response failed:, administrator response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigAdministratorObject(administrator);
            setMinimumPasswordLength(minimumPasswordLength);
            setMaximumPasswordLength(maximumPasswordLength);
            setAdministratorObject(administrator);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [administratorId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);
    
    const setAdministratorData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setAdministratorObject(administratorObj => produce(administratorObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const verifyData = useCallback(() => {
        setIsInnerPageLoading(true);
        Api.verifySingleObject(ObjectTypes.ADMINISTRATOR, administratorId, administratorObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [administratorId, getMessagesArrayByPages, administratorObject, pageIdToPageName]);

    const onPageChange = useCallback((pageId) => {
        verifyData();
        setChosenPageId(pageId);
    }, [verifyData]);

    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.ADMINISTRATOR, administratorId, administratorObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchAdministrator();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [getErrorMessage, administratorId, fetchAdministrator, administratorObject, enqueueSuccessSnackbar, t]);

    const deleteButtonOnClick = useCallback(() => {
        if (window.confirm(t('OBJECT_EDITOR_OBJ_DELETE', [administratorId] ))) {
            Api.deleteObjects(ObjectTypes.ADMINISTRATOR, [administratorId])
            .then(res => {
                const deleteResult = res.data.results[0];
                if (deleteResult.success) {
                    setIsObjectDirty(false); // Set as false to not trigger dirty modal
                    store.dispatch(push(SMCPaths.administrators));
                    enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_DELETE_OK', [administratorId]));
                } else {
                    const deleteErrorCode = deleteResult.errorCode;
                    let deleteErrorParams;
                    if (deleteResult.errorParams) {
                        deleteErrorParams = deleteResult.errorParams.map(param => (
                            isStringI18n(param)) ? t(extractI18nString(param)) : param
                        );
                    }
                    enqueueErrorSnackbar(t(extractI18nString(deleteErrorCode), deleteErrorParams));
                }
            })
            .catch(err => {
                const errorCode = getErrorMessage(err);
                enqueueErrorSnackbar(t(errorCode));
            });
        }
    }, [administratorId, getErrorMessage, enqueueSuccessSnackbar, enqueueErrorSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setAdministratorObject(origAdministratorObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origAdministratorObject]);

    const reloadAdministratorStatus = useCallback(() => {
        Api.fetchSingleObject(ObjectTypes.ADMINISTRATOR, administratorId)
        .then(resp => {
            const administrator = resp.data.data.administrator;
            const statusPage = administrator[AdministratorGetSchema.pages.status.pageId];
            setAdministratorData(AdministratorGetSchema.pages.status.pageId, statusPage);
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_REFRESHED_OK'));
        })
        .catch(e => {
            enqueueErrorSnackbar('Error reloading status page');
        })
    }, [administratorId, setAdministratorData, enqueueErrorSnackbar, enqueueSuccessSnackbar, t]);

    // Fetch administrator on loading of page
    useEffect(() => {
        (async () => {
            try {
                const fetchAdministratorResp = await Api.fetchSingleObject(ObjectTypes.ADMINISTRATOR, administratorId);
                const administrator = fetchAdministratorResp.data.data.administrator;
                // Validate server response
                if (!administrator) {
                    console.error('Get administrator response failed:, administrator response returned empty');
                    throw new Error(ErrorCodes.SERVER_ERROR);
                }
                setOrigAdministratorObject(administrator);
                setAdministratorObject(administrator);
                setIsPageLoadFailed(false);
            } catch (e) {
                const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
                if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                    enqueueLogoutSnackbar(t(errorCode));
                } else {
                    enqueueErrorSnackbar(t(errorCode));
                }
                setIsPageLoadFailed(true);
            }
            setIsPageDataLoaded(true);
        })();
    }, [administratorId, enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);
    
    return {
        administratorObject,
        isObjectDirty,
        editorPages,
        helpId,
        origAdministratorObject,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setAdministratorData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchAdministrator,
        reloadAdministratorStatus,
        minimumPasswordLength,
        maximumPasswordLength
    };
}